import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES } from '../../../constants/routes';
import axios from 'axios';

/** Split one CSV line; supports quoted fields and doubled quotes. */
function splitCsvRow(line) {
  const out = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === ',' && !inQuotes) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  out.push(cur.trim());
  return out.map((s) => {
    let t = s;
    if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
      t = t.slice(1, -1).replace(/""/g, '"');
    }
    return t.trim();
  });
}

/** Parse Request Types.csv: Title (required); Category optional. */
function parseRequestTypeImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iTitle =
    headerParts.indexOf('title') >= 0
      ? headerParts.indexOf('title')
      : headerParts.indexOf('name') >= 0
        ? headerParts.indexOf('name')
        : headerParts.indexOf('request type');
  if (iTitle === -1) {
    return {
      error: `Missing required column. Need Title (or name). Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({ title: (parts[iTitle] || '').trim() });
  }
  return { rows };
}

/** Parse status import CSV: Title (required); also accepts name or status. */
function parseStatusImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iTitle =
    headerParts.indexOf('title') >= 0
      ? headerParts.indexOf('title')
      : headerParts.indexOf('name') >= 0
        ? headerParts.indexOf('name')
        : headerParts.indexOf('status');
  if (iTitle === -1) {
    return {
      error: `Missing required column. Need Title (or name). Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({ title: (parts[iTitle] || '').trim() });
  }
  return { rows };
}

function downloadStatusImportTemplate() {
  const csv =
    'Title\nOpen\nIn Progress\nCompleted\nFollow Up\nCancelled\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'statuses-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadRequestTypeImportTemplate() {
  const csv = 'Title,Category\nAC,TECHS\nBlinds,TECHS\nLeak,TECHS\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'request-types-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Parse PSR Requester.csv: Title (required); optional Enabled (Yes/No). */
function parseRequesterImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iTitle =
    headerParts.indexOf('title') >= 0
      ? headerParts.indexOf('title')
      : headerParts.indexOf('name') >= 0
        ? headerParts.indexOf('name')
        : headerParts.indexOf('requester');
  const iEnabled = headerParts.indexOf('enabled');
  if (iTitle === -1) {
    return {
      error: `Missing required column. Need Title (or name). Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    const row = { title: (parts[iTitle] || '').trim() };
    if (iEnabled >= 0) {
      row.enabled = (parts[iEnabled] || '').trim();
    }
    rows.push(row);
  }
  return { rows };
}

function downloadRequesterImportTemplate() {
  const csv =
    'Title,Enabled\nFront Desk,Yes\nWelcome Center,Yes\nGuest,Yes\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'requesters-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Parse PSR Assigned.csv: Title column (technician name). */
function parseAssignedImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const iTitle =
    headerParts.indexOf('title') >= 0
      ? headerParts.indexOf('title')
      : headerParts.indexOf('technician') >= 0
        ? headerParts.indexOf('technician')
        : headerParts.indexOf('name');
  if (iTitle === -1) {
    return {
      error: `Missing required column. Need Title (or technician). Found: ${headerParts.join(', ')}`,
    };
  }
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({ title: (parts[iTitle] || '').trim() });
  }
  return { rows };
}

function downloadAssignedImportTemplate() {
  const csv = 'Title\nGrounds\nTech 1\nMaintenance\n';
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'assigned-import-template.csv';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function csvColIndex(headerParts, names) {
  for (const n of names) {
    const i = headerParts.indexOf(n);
    if (i >= 0) return i;
  }
  return -1;
}

/** Parse Service Requests List.csv (SharePoint export). */
function parseServiceRequestImportCsv(text) {
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  if (lines.length < 2) {
    return { error: 'File needs a header row plus at least one data row.' };
  }
  const headerParts = splitCsvRow(lines[0].replace(/^\uFEFF/, '')).map((h) => h.trim().toLowerCase());
  const cols = {
    title: csvColIndex(headerParts, ['title']),
    request_type: csvColIndex(headerParts, ['request type']),
    location: csvColIndex(headerParts, ['location']),
    requester: csvColIndex(headerParts, ['requester']),
    requester_name: csvColIndex(headerParts, ['requester name']),
    date: csvColIndex(headerParts, ['date']),
    permission: csvColIndex(headerParts, ['permission']),
    occupied: csvColIndex(headerParts, ['occupied']),
    priority: csvColIndex(headerParts, ['priority']),
    problem: csvColIndex(headerParts, ['problem']),
    notes: csvColIndex(headerParts, ['notes']),
    task_status: csvColIndex(headerParts, ['task status', 'status']),
    assigned: csvColIndex(headerParts, ['assigned']),
    solution: csvColIndex(headerParts, ['solution']),
    date_finished: csvColIndex(headerParts, ['date finished']),
    follow_up: csvColIndex(headerParts, ['follow up', 'followup']),
  };
  if (cols.problem === -1) {
    return {
      error: `Missing required column Problem. Found: ${headerParts.join(', ')}`,
    };
  }
  const pick = (parts, idx) => (idx >= 0 ? (parts[idx] || '').trim() : '');
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const parts = splitCsvRow(lines[i]);
    rows.push({
      title: pick(parts, cols.title),
      request_type: pick(parts, cols.request_type),
      location: pick(parts, cols.location),
      requester: pick(parts, cols.requester),
      requester_name: pick(parts, cols.requester_name),
      date: pick(parts, cols.date),
      permission: pick(parts, cols.permission),
      occupied: pick(parts, cols.occupied),
      priority: pick(parts, cols.priority),
      problem: pick(parts, cols.problem),
      notes: pick(parts, cols.notes),
      task_status: pick(parts, cols.task_status),
      assigned: pick(parts, cols.assigned),
      solution: pick(parts, cols.solution),
      date_finished: pick(parts, cols.date_finished),
      follow_up: pick(parts, cols.follow_up),
    });
  }
  return { rows };
}

function Admin() {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeMenu, setActiveMenu] = useState('Users');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);
  const [showEditPropertyModal, setShowEditPropertyModal] = useState(false);
  const [showDeletePropertyModal, setShowDeletePropertyModal] = useState(false);
  const [showAddStatusModal, setShowAddStatusModal] = useState(false);
  const [showImportStatusesModal, setShowImportStatusesModal] = useState(false);
  const [statusImportPropertyId, setStatusImportPropertyId] = useState('');
  const [statusImportParseError, setStatusImportParseError] = useState('');
  const [statusImportRows, setStatusImportRows] = useState([]);
  const [statusImportResult, setStatusImportResult] = useState(null);
  const [statusImportBusy, setStatusImportBusy] = useState(false);
  const [showAddRequestTypeModal, setShowAddRequestTypeModal] = useState(false);
  const [showAddLocationModal, setShowAddLocationModal] = useState(false);
  const [showAddRequesterModal, setShowAddRequesterModal] = useState(false);
  const [showAddAssignedModal, setShowAddAssignedModal] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [showDeleteStatusModal, setShowDeleteStatusModal] = useState(false);
  const [showEditRequestTypeModal, setShowEditRequestTypeModal] = useState(false);
  const [showDeleteRequestTypeModal, setShowDeleteRequestTypeModal] = useState(false);
  const [showImportRequestTypesModal, setShowImportRequestTypesModal] = useState(false);
  const [requestTypeImportPropertyId, setRequestTypeImportPropertyId] = useState('');
  const [requestTypeImportParseError, setRequestTypeImportParseError] = useState('');
  const [requestTypeImportRows, setRequestTypeImportRows] = useState([]);
  const [requestTypeImportResult, setRequestTypeImportResult] = useState(null);
  const [requestTypeImportBusy, setRequestTypeImportBusy] = useState(false);
  const [showEditLocationModal, setShowEditLocationModal] = useState(false);
  const [showDeleteLocationModal, setShowDeleteLocationModal] = useState(false);
  const [showEditRequesterModal, setShowEditRequesterModal] = useState(false);
  const [showDeleteRequesterModal, setShowDeleteRequesterModal] = useState(false);
  const [showImportRequestersModal, setShowImportRequestersModal] = useState(false);
  const [requesterImportPropertyId, setRequesterImportPropertyId] = useState('');
  const [requesterImportParseError, setRequesterImportParseError] = useState('');
  const [requesterImportRows, setRequesterImportRows] = useState([]);
  const [requesterImportResult, setRequesterImportResult] = useState(null);
  const [requesterImportBusy, setRequesterImportBusy] = useState(false);
  const [showImportAssignedModal, setShowImportAssignedModal] = useState(false);
  const [assignedImportPropertyId, setAssignedImportPropertyId] = useState('');
  const [assignedImportParseError, setAssignedImportParseError] = useState('');
  const [assignedImportRows, setAssignedImportRows] = useState([]);
  const [assignedImportResult, setAssignedImportResult] = useState(null);
  const [assignedImportBusy, setAssignedImportBusy] = useState(false);
  const [srImportPropertyId, setSrImportPropertyId] = useState('');
  const [srImportParseError, setSrImportParseError] = useState('');
  const [srImportRows, setSrImportRows] = useState([]);
  const [srImportResult, setSrImportResult] = useState(null);
  const [srImportBusy, setSrImportBusy] = useState(false);
  const [showEditAssignedModal, setShowEditAssignedModal] = useState(false);
  const [showDeleteAssignedModal, setShowDeleteAssignedModal] = useState(false);
  
  const [properties, setProperties] = useState([]);
  const [users, setUsers] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusPropertyFilter, setStatusPropertyFilter] = useState('');
  const [requestTypes, setRequestTypes] = useState([]);
  const [requestTypePropertyFilter, setRequestTypePropertyFilter] = useState('');
  const [locations, setLocations] = useState([]);
  const [locationPropertyFilter, setLocationPropertyFilter] = useState('');
  const [requesters, setRequesters] = useState([]);
  const [requesterPropertyFilter, setRequesterPropertyFilter] = useState('');
  const [assigned, setAssigned] = useState([]);
  const [assignedPropertyFilter, setAssignedPropertyFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role_id: '',
    property_id: ''
  });
  const [editingUser, setEditingUser] = useState({
    id: null,
    name: '',
    email: '',
    password: '',
    role_id: '',
    property_id: ''
  });
  const [deletingUser, setDeletingUser] = useState({
    id: null,
    name: ''
  });
  const [editingProperty, setEditingProperty] = useState({
    id: null,
    name: '',
    address: '',
    property_code: '',
  });
  const [deletingProperty, setDeletingProperty] = useState({
    id: null,
    name: ''
  });
  const [newProperty, setNewProperty] = useState({
    name: '',
    address: '',
    property_code: '',
  });
  const [newStatus, setNewStatus] = useState({
    name: '',
    property_id: ''
  });
  const [newRequestType, setNewRequestType] = useState({
    name: '',
    property_id: ''
  });
  const [newLocation, setNewLocation] = useState({
    name: '',
    site_address: '',
    property_id: ''
  });
  const [newRequester, setNewRequester] = useState({
    name: '',
    contact: '',
    property_id: ''
  });
  const [newAssigned, setNewAssigned] = useState({
    technician: '',
    assignedRequests: 0,
    property_id: ''
  });
  const [editingStatus, setEditingStatus] = useState({
    id: null,
    name: '',
    property_id: ''
  });
  const [deletingStatus, setDeletingStatus] = useState({
    id: null,
    name: ''
  });
  const [editingRequestType, setEditingRequestType] = useState({
    id: null,
    name: '',
    property_id: ''
  });
  const [deletingRequestType, setDeletingRequestType] = useState({
    id: null,
    name: ''
  });
  const [editingLocation, setEditingLocation] = useState({
    id: null,
    name: '',
    site_address: '',
    property_id: ''
  });
  const [deletingLocation, setDeletingLocation] = useState({
    id: null,
    name: ''
  });
  const [editingRequester, setEditingRequester] = useState({
    id: null,
    name: '',
    contact: '',
    property_id: ''
  });
  const [deletingRequester, setDeletingRequester] = useState({
    id: null,
    name: ''
  });
  const [editingAssigned, setEditingAssigned] = useState({
    id: null,
    technician: '',
    assignedRequests: 0,
    property_id: ''
  });
  const [deletingAssigned, setDeletingAssigned] = useState({
    id: null,
    technician: ''
  });

  // Fetch data on component mount
  useEffect(() => {
    fetchProperties();
    fetchUsers();
    fetchRoles();
    fetchStatuses();
    fetchRequestTypes();
    fetchLocations();
    fetchRequesters();
    fetchAssigned();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProperties(response.data);
    } catch (err) {
      console.error('Failed to fetch properties:', err);
      // For demo purposes, add some sample properties
      setProperties([
        { id: 1, name: 'Building A', address: '123 Main St' },
        { id: 2, name: 'Building B', address: '456 Oak Ave' },
        { id: 3, name: 'Building C', address: '789 Pine Rd' }
      ]);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get('/api/admin/roles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRolesList(response.data);
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setRolesList([]);
    }
  };

  const roleKeyById = (rid) => rolesList.find((r) => Number(r.id) === Number(rid))?.key;

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setUsers([]);
    }
  };

  const fetchStatuses = async () => {
    try {
      const response = await axios.get('/api/statuses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStatuses(response.data);
    } catch (err) {
      console.error('Failed to fetch statuses:', err);
      setStatuses([]);
    }
  };

  const fetchRequestTypes = async () => {
    try {
      const response = await axios.get('/api/request-types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequestTypes(response.data);
    } catch (err) {
      console.error('Failed to fetch request types:', err);
      setRequestTypes([]);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get('/api/locations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLocations(response.data);
    } catch (err) {
      console.error('Failed to fetch locations:', err);
      setLocations([]);
    }
  };

  const fetchRequesters = async () => {
    try {
      const response = await axios.get('/api/requesters', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequesters(response.data);
    } catch (err) {
      console.error('Failed to fetch requesters:', err);
      setRequesters([]);
    }
  };

  const fetchAssigned = async () => {
    try {
      const response = await axios.get('/api/assigned', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setAssigned(response.data);
    } catch (err) {
      console.error('Failed to fetch assigned:', err);
      setAssigned([]);
    }
  };

  const handleLogout = () => {
    logout();
    navigate(ROUTES.LOGIN);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const rk = roleKeyById(newUser.role_id);
      if (rk === 'manager' && !newUser.property_id) {
        setError('Managers must have a property assigned');
        setLoading(false);
        return;
      }
      await axios.post('/api/users', {
        name: newUser.name,
        email: newUser.email,
        password: newUser.password,
        role_id: Number(newUser.role_id),
        property_id: newUser.property_id || null,
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const defaultRid = rolesList.find((x) => x.key === 'user')?.id || '';
      setNewUser({ name: '', email: '', password: '', role_id: defaultRid, property_id: '' });
      setShowAddUserModal(false);
      // Refresh users list
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const rk = roleKeyById(editingUser.role_id);
      if (rk === 'manager' && !editingUser.property_id) {
        setError('Managers must have a property assigned');
        setLoading(false);
        return;
      }
      const updateData = {
        name: editingUser.name,
        email: editingUser.email,
        role_id: Number(editingUser.role_id),
        property_id: editingUser.property_id || null,
      };

      // Only include password if it's not empty
      if (editingUser.password) {
        updateData.password = editingUser.password;
      }

      await axios.put(`/api/users/${editingUser.id}`, updateData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      setEditingUser({ id: null, name: '', email: '', password: '', role_id: '', property_id: '' });
      setShowEditUserModal(false);
      // Refresh users list
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/users/${deletingUser.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingUser({ id: null, name: '' });
      setShowDeleteUserModal(false);
      // Refresh users list
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (userRow) => {
    setEditingUser({
      id: userRow.id,
      name: userRow.name,
      email: userRow.email,
      password: '', // Don't pre-fill password
      role_id: userRow.role_id,
      property_id: userRow.property_id || ''
    });
    setShowEditUserModal(true);
  };

  const openDeleteModal = (user) => {
    setDeletingUser({
      id: user.id,
      name: user.name
    });
    setShowDeleteUserModal(true);
  };

  const openEditPropertyModal = (property) => {
    setEditingProperty({
      id: property.id,
      name: property.name,
      address: property.address || '',
      property_code: property.property_code || '',
    });
    setShowEditPropertyModal(true);
  };

  const openDeletePropertyModal = (property) => {
    setDeletingProperty({
      id: property.id,
      name: property.name
    });
    setShowDeletePropertyModal(true);
  };

  // Status modal functions
  const openEditStatusModal = (status) => {
    setEditingStatus({
      id: status.id,
      name: status.name,
      property_id: status.property_id
    });
    setShowEditStatusModal(true);
  };

  const openDeleteStatusModal = (status) => {
    setDeletingStatus({
      id: status.id,
      name: status.name
    });
    setShowDeleteStatusModal(true);
  };

  // Request Type modal functions
  const openEditRequestTypeModal = (requestType) => {
    setEditingRequestType({
      id: requestType.id,
      name: requestType.name,
      property_id: requestType.property_id
    });
    setShowEditRequestTypeModal(true);
  };

  const openDeleteRequestTypeModal = (requestType) => {
    setDeletingRequestType({
      id: requestType.id,
      name: requestType.name
    });
    setShowDeleteRequestTypeModal(true);
  };

  // Location modal functions
  const openEditLocationModal = (location) => {
    setEditingLocation({
      id: location.id,
      name: location.site_name || location.name,
      site_address: location.site_address || '',
      property_id: location.property_id
    });
    setShowEditLocationModal(true);
  };

  const openDeleteLocationModal = (location) => {
    setDeletingLocation({
      id: location.id,
      name: location.name
    });
    setShowDeleteLocationModal(true);
  };

  // Requester modal functions
  const openEditRequesterModal = (requester) => {
    setEditingRequester({
      id: requester.id,
      name: requester.name,
      contact: requester.contact,
      property_id: requester.property_id
    });
    setShowEditRequesterModal(true);
  };

  const openDeleteRequesterModal = (requester) => {
    setDeletingRequester({
      id: requester.id,
      name: requester.name
    });
    setShowDeleteRequesterModal(true);
  };

  // Assigned modal functions
  const openEditAssignedModal = (assignedItem) => {
    setEditingAssigned({
      id: assignedItem.id,
      technician: assignedItem.technician,
      assignedRequests: assignedItem.assigned_requests,
      property_id: assignedItem.property_id
    });
    setShowEditAssignedModal(true);
  };

  const openDeleteAssignedModal = (assignedItem) => {
    setDeletingAssigned({
      id: assignedItem.id,
      technician: assignedItem.technician
    });
    setShowDeleteAssignedModal(true);
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/properties', newProperty, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewProperty({ name: '', address: '', property_code: '' });
      setShowAddPropertyModal(false);
      // Refresh properties list
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create property');
    } finally {
      setLoading(false);
    }
  };

  const handleEditProperty = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/properties/${editingProperty.id}`, editingProperty, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingProperty({ id: null, name: '', address: '', property_code: '' });
      setShowEditPropertyModal(false);
      // Refresh properties list
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update property');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/properties/${deletingProperty.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingProperty({ id: null, name: '' });
      setShowDeletePropertyModal(false);
      // Refresh properties list
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete property');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStatus = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/statuses', newStatus, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewStatus({ name: '', property_id: '' });
      setShowAddStatusModal(false);
      // Refresh statuses list
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create status');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequestType = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/request-types', newRequestType, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewRequestType({ name: '', property_id: '' });
      setShowAddRequestTypeModal(false);
      // Refresh request types list
      fetchRequestTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create request type');
    } finally {
      setLoading(false);
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/locations', newLocation, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewLocation({ name: '', site_address: '', property_id: '' });
      setShowAddLocationModal(false);
      // Refresh locations list
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create location');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRequester = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/requesters', newRequester, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewRequester({ name: '', contact: '', property_id: '' });
      setShowAddRequesterModal(false);
      // Refresh requesters list
      fetchRequesters();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create requester');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAssigned = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.post('/api/assigned', newAssigned, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setNewAssigned({ technician: '', assignedRequests: 0, property_id: '' });
      setShowAddAssignedModal(false);
      // Refresh assigned list
      fetchAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create assignment');
    } finally {
      setLoading(false);
    }
  };

  // Status handlers
  const handleEditStatus = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/statuses/${editingStatus.id}`, editingStatus, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingStatus({ id: null, name: '', property_id: '' });
      setShowEditStatusModal(false);
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStatus = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/statuses/${deletingStatus.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingStatus({ id: null, name: '' });
      setShowDeleteStatusModal(false);
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete status');
    } finally {
      setLoading(false);
    }
  };

  // Request Type handlers
  const handleEditRequestType = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/request-types/${editingRequestType.id}`, editingRequestType, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingRequestType({ id: null, name: '', property_id: '' });
      setShowEditRequestTypeModal(false);
      fetchRequestTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update request type');
    } finally {
      setLoading(false);
    }
  };

  const openImportStatusesModal = () => {
    setStatusImportPropertyId(statusPropertyFilter || '');
    setStatusImportParseError('');
    setStatusImportRows([]);
    setStatusImportResult(null);
    setShowImportStatusesModal(true);
  };

  const handleImportStatusesFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setStatusImportResult(null);
    setStatusImportParseError('');
    setStatusImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseStatusImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setStatusImportParseError(parsed.error);
        return;
      }
      setStatusImportRows(parsed.rows);
    };
    reader.onerror = () => setStatusImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunStatusImport = async () => {
    if (!statusImportPropertyId || !statusImportRows.length) return;
    setStatusImportBusy(true);
    setError('');
    setStatusImportResult(null);
    try {
      const res = await axios.post(
        '/api/statuses/import',
        {
          property_id: Number(statusImportPropertyId),
          rows: statusImportRows,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setStatusImportResult(res.data);
      fetchStatuses();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setStatusImportBusy(false);
    }
  };

  const openImportRequestTypesModal = () => {
    setRequestTypeImportPropertyId(requestTypePropertyFilter || '');
    setRequestTypeImportParseError('');
    setRequestTypeImportRows([]);
    setRequestTypeImportResult(null);
    setShowImportRequestTypesModal(true);
  };

  const handleImportRequestTypesFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setRequestTypeImportResult(null);
    setRequestTypeImportParseError('');
    setRequestTypeImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseRequestTypeImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setRequestTypeImportParseError(parsed.error);
        return;
      }
      setRequestTypeImportRows(parsed.rows);
    };
    reader.onerror = () => setRequestTypeImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunRequestTypeImport = async () => {
    if (!requestTypeImportPropertyId || !requestTypeImportRows.length) return;
    setRequestTypeImportBusy(true);
    setError('');
    setRequestTypeImportResult(null);
    try {
      const res = await axios.post(
        '/api/request-types/import',
        {
          property_id: Number(requestTypeImportPropertyId),
          rows: requestTypeImportRows,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setRequestTypeImportResult(res.data);
      fetchRequestTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setRequestTypeImportBusy(false);
    }
  };

  const openImportRequestersModal = () => {
    setRequesterImportPropertyId(requesterPropertyFilter || '');
    setRequesterImportParseError('');
    setRequesterImportRows([]);
    setRequesterImportResult(null);
    setShowImportRequestersModal(true);
  };

  const handleImportRequestersFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setRequesterImportResult(null);
    setRequesterImportParseError('');
    setRequesterImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseRequesterImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setRequesterImportParseError(parsed.error);
        return;
      }
      setRequesterImportRows(parsed.rows);
    };
    reader.onerror = () => setRequesterImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunRequesterImport = async () => {
    if (!requesterImportPropertyId || !requesterImportRows.length) return;
    setRequesterImportBusy(true);
    setError('');
    setRequesterImportResult(null);
    try {
      const res = await axios.post(
        '/api/requesters/import',
        {
          property_id: Number(requesterImportPropertyId),
          rows: requesterImportRows,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setRequesterImportResult(res.data);
      fetchRequesters();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setRequesterImportBusy(false);
    }
  };

  const openImportAssignedModal = () => {
    setAssignedImportPropertyId(assignedPropertyFilter || '');
    setAssignedImportParseError('');
    setAssignedImportRows([]);
    setAssignedImportResult(null);
    setShowImportAssignedModal(true);
  };

  const handleImportAssignedFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAssignedImportResult(null);
    setAssignedImportParseError('');
    setAssignedImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseAssignedImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setAssignedImportParseError(parsed.error);
        return;
      }
      setAssignedImportRows(parsed.rows);
    };
    reader.onerror = () => setAssignedImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunAssignedImport = async () => {
    if (!assignedImportPropertyId || !assignedImportRows.length) return;
    setAssignedImportBusy(true);
    setError('');
    setAssignedImportResult(null);
    try {
      const res = await axios.post(
        '/api/assigned/import',
        {
          property_id: Number(assignedImportPropertyId),
          rows: assignedImportRows,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        }
      );
      setAssignedImportResult(res.data);
      fetchAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Import failed');
    } finally {
      setAssignedImportBusy(false);
    }
  };

  const handleServiceRequestImportFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setSrImportResult(null);
    setSrImportParseError('');
    setSrImportRows([]);
    const reader = new FileReader();
    reader.onload = () => {
      const parsed = parseServiceRequestImportCsv(String(reader.result || ''));
      if (parsed.error) {
        setSrImportParseError(parsed.error);
        return;
      }
      setSrImportRows(parsed.rows);
    };
    reader.onerror = () => setSrImportParseError('Could not read file.');
    reader.readAsText(file);
  };

  const handleRunServiceRequestImport = async () => {
    if (!srImportPropertyId || !srImportRows.length) return;
    setSrImportBusy(true);
    setError('');
    setSrImportResult(null);
    const authHeaders = {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
    };
    const propertyId = Number(srImportPropertyId);
    const batchSize = 100;
    const merged = { created: 0, skipped: 0, errors: [] };
    try {
      for (let offset = 0; offset < srImportRows.length; offset += batchSize) {
        const chunk = srImportRows.slice(offset, offset + batchSize);
        const res = await axios.post(
          '/api/requests/import',
          { property_id: propertyId, rows: chunk },
          { headers: authHeaders, timeout: 300000 }
        );
        const part = res.data;
        merged.created += part.created || 0;
        merged.skipped += part.skipped || 0;
        if (part.errors?.length) {
          for (const er of part.errors) {
            merged.errors.push({
              ...er,
              line: er.line != null ? er.line + offset : er.line,
            });
          }
        }
      }
      setSrImportResult(merged);
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        (err.code === 'ERR_NETWORK'
          ? 'Network error (often CORS, timeout, or API unavailable). Check API CORS_ALLOWED_ORIGINS includes this site URL.'
          : err.message) ||
        'Import failed';
      setError(msg);
    } finally {
      setSrImportBusy(false);
    }
  };

  const handleDeleteRequestType = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/request-types/${deletingRequestType.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingRequestType({ id: null, name: '' });
      setShowDeleteRequestTypeModal(false);
      fetchRequestTypes();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete request type');
    } finally {
      setLoading(false);
    }
  };

  // Location handlers
  const handleEditLocation = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/locations/${editingLocation.id}`, editingLocation, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingLocation({ id: null, name: '', site_address: '', property_id: '' });
      setShowEditLocationModal(false);
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLocation = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/locations/${deletingLocation.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingLocation({ id: null, name: '' });
      setShowDeleteLocationModal(false);
      fetchLocations();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete location');
    } finally {
      setLoading(false);
    }
  };

  // Requester handlers
  const handleEditRequester = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/requesters/${editingRequester.id}`, editingRequester, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingRequester({ id: null, name: '', contact: '', property_id: '' });
      setShowEditRequesterModal(false);
      fetchRequesters();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update requester');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRequester = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/requesters/${deletingRequester.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingRequester({ id: null, name: '' });
      setShowDeleteRequesterModal(false);
      fetchRequesters();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete requester');
    } finally {
      setLoading(false);
    }
  };

  // Assigned handlers
  const handleEditAssigned = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await axios.put(`/api/assigned/${editingAssigned.id}`, editingAssigned, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setEditingAssigned({ id: null, technician: '', assignedRequests: 0, property_id: '' });
      setShowEditAssignedModal(false);
      fetchAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAssigned = async () => {
    setLoading(true);
    setError('');

    try {
      await axios.delete(`/api/assigned/${deletingAssigned.id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setDeletingAssigned({ id: null, technician: '' });
      setShowDeleteAssignedModal(false);
      fetchAssigned();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete assignment');
    } finally {
      setLoading(false);
    }
  };

  const menuItems = [
    'Service Requests',
    'Status',
    'Request Type',
    'Location',
    'Requester',
    'Assigned',
  ];

  const filterByProperty = (rows, propertyId) => {
    if (!propertyId) return rows;
    return rows.filter((row) => String(row.property_id) === String(propertyId));
  };

  const renderPropertyFilter = (filterValue, setFilter, filteredCount, itemLabel) => (
    <div className="row g-2 mb-3">
      <div className="col-md-4">
        <label className="form-label small mb-1">Filter by property</label>
        <select
          className="form-select form-select-sm"
          value={filterValue}
          onChange={(e) => setFilter(e.target.value)}
        >
          <option value="">All properties</option>
          {properties.map((property) => (
            <option key={property.id} value={property.id}>
              {property.name}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4 d-flex align-items-end">
        <span className="small text-muted">
          {filteredCount} {itemLabel}{filteredCount === 1 ? '' : 's'}
        </span>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeMenu) {
      case 'Users':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>User Management</h3>
              <button 
                className="btn btn-primary"
                onClick={() => {
                  const defaultRid = rolesList.find((x) => x.key === 'user')?.id || '';
                  setNewUser({ name: '', email: '', password: '', role_id: defaultRid, property_id: '' });
                  setShowAddUserModal(true);
                }}
              >
                Add User
              </button>
            </div>
            <p>Manage system users, roles, and permissions.</p>
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length > 0 ? (
                    users.map(userItem => (
                      <tr key={userItem.id}>
                        <td>{userItem.id}</td>
                        <td>{userItem.name}</td>
                        <td>{userItem.email}</td>
                        <td>{userItem.role_key || userItem.role}</td>
                        <td>{userItem.property_name || 'No Property'}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditModal(userItem)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteModal(userItem)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                                          <td colSpan="6" className="text-center text-muted">
                      No users found
                    </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case 'Properties':
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3>Property Management</h3>
              <button 
                className="btn btn-primary"
                onClick={() => setShowAddPropertyModal(true)}
              >
                Add Property
              </button>
            </div>
            <p>Manage properties and their configurations.</p>
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Property Name</th>
                    <th>Property code</th>
                    <th>Address</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {properties.length > 0 ? (
                    properties.map(property => (
                      <tr key={property.id}>
                        <td>{property.id}</td>
                        <td>{property.name}</td>
                        <td>{property.property_code || '—'}</td>
                        <td>{property.address}</td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditPropertyModal(property)}
                          >
                            Edit
                          </button>
                          <button 
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeletePropertyModal(property)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        No properties found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );

      case 'Service Requests': {
        return (
          <div>
            <h3 className="mb-2">Import service requests</h3>
            <p className="text-muted small">
              Upload a CSV like <strong>Service Requests List.csv</strong> (SharePoint export). Select the
              property for all rows. Request Type, Location, Requester, Task Status, and Assigned are matched
              by name to that property&apos;s lookup tables (case-insensitive). Import request types, locations,
              requesters, statuses (Status tab import), and assigned technicians first.
            </p>
            <div className="row g-3 mb-3">
              <div className="col-md-5">
                <label className="form-label">Property</label>
                <select
                  className="form-select"
                  value={srImportPropertyId}
                  onChange={(e) => setSrImportPropertyId(e.target.value)}
                >
                  <option value="">Select property…</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {property.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-7">
                <label className="form-label">CSV file</label>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="form-control"
                  onChange={handleServiceRequestImportFile}
                />
              </div>
            </div>
            {srImportParseError && (
              <div className="alert alert-danger py-2 small">{srImportParseError}</div>
            )}
            {srImportRows.length > 0 && !srImportParseError && (
              <div className="alert alert-info py-2 small">
                Parsed <strong>{srImportRows.length}</strong> row{srImportRows.length === 1 ? '' : 's'}.
                Import runs in batches of 200. Required: Problem, Request Type, Location, Requester
                (name must exist for the property).
              </div>
            )}
            <button
              type="button"
              className="btn btn-primary mb-3"
              disabled={srImportBusy || !srImportPropertyId || !srImportRows.length || !!srImportParseError}
              onClick={handleRunServiceRequestImport}
            >
              {srImportBusy ? 'Importing…' : 'Run import'}
            </button>
            {srImportResult && (
              <div>
                <p className="small mb-2">
                  <strong>{srImportResult.created}</strong> created,{' '}
                  <strong>{srImportResult.skipped}</strong> skipped.
                  {srImportResult.errors?.length > 0 && (
                    <span className="text-muted">
                      {' '}
                      ({srImportResult.errors.length} note
                      {srImportResult.errors.length === 1 ? '' : 's'}
                      {srImportResult.errors.some((e) => e.error?.includes('warning')) ? ', incl. lookup warnings' : ''}
                      )
                    </span>
                  )}
                </p>
                {srImportResult.errors?.length > 0 && (
                  <>
                    <div
                      className="app-table-wrap table-responsive border"
                      style={{ maxHeight: 'min(70vh, 520px)', overflow: 'auto' }}
                      role="region"
                      aria-label="Import result messages"
                    >
                      <table className="table table-sm table-bordered mb-0">
                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr>
                            <th style={{ width: '4.5rem' }}>Row</th>
                            <th style={{ minWidth: '8rem' }}>Title / Problem</th>
                            <th>Detail</th>
                          </tr>
                        </thead>
                        <tbody>
                          {srImportResult.errors.map((er, idx) => (
                            <tr key={`${er.line}-${idx}`}>
                              <td>{er.line}</td>
                              <td className="small">{er.title ?? '—'}</td>
                              <td className="small text-break">{er.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="small text-muted mt-1 mb-0">
                      Scroll the table to see all {srImportResult.errors.length} message
                      {srImportResult.errors.length === 1 ? '' : 's'}.
                    </p>
                  </>
                )}
              </div>
            )}
          </div>
        );
      }

      case 'Status': {
        const filteredStatuses = filterByProperty(statuses, statusPropertyFilter);
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Status Management</h3>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openImportStatusesModal}
                >
                  Import from CSV
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddStatusModal(true)}
                >
                  Add Status
                </button>
              </div>
            </div>
            <p className="text-muted small">Manage service request statuses by property.</p>
            {renderPropertyFilter(statusPropertyFilter, setStatusPropertyFilter, filteredStatuses.length, 'status')}
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Status Name</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStatuses.length > 0 ? (
                    filteredStatuses.map((status) => (
                      <tr key={status.id}>
                        <td>{status.id}</td>
                        <td>{status.name}</td>
                        <td>{status.property_name}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditStatusModal(status)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteStatusModal(status)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        {statuses.length > 0
                          ? 'No statuses for this property'
                          : 'No statuses found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'Request Type': {
        const filteredRequestTypes = filterByProperty(requestTypes, requestTypePropertyFilter);
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Request Type Management</h3>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openImportRequestTypesModal}
                >
                  Import from CSV
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddRequestTypeModal(true)}
                >
                  Add Request Type
                </button>
              </div>
            </div>
            <p className="text-muted small">Manage different types of service requests by property.</p>
            {renderPropertyFilter(
              requestTypePropertyFilter,
              setRequestTypePropertyFilter,
              filteredRequestTypes.length,
              'request type'
            )}
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Request Type</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequestTypes.length > 0 ? (
                    filteredRequestTypes.map((requestType) => (
                      <tr key={requestType.id}>
                        <td>{requestType.id}</td>
                        <td>{requestType.name}</td>
                        <td>{requestType.property_name}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditRequestTypeModal(requestType)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteRequestTypeModal(requestType)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="text-center text-muted">
                        {requestTypes.length > 0
                          ? 'No request types for this property'
                          : 'No request types found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'Location': {
        const filteredLocations = locationPropertyFilter
          ? locations.filter(
              (loc) => String(loc.property_id) === String(locationPropertyFilter)
            )
          : locations;
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Location Management</h3>
              <button
                className="btn btn-primary"
                onClick={() => setShowAddLocationModal(true)}
              >
                Add Location
              </button>
            </div>
            <p className="text-muted small">Manage locations within properties.</p>
            {renderPropertyFilter(locationPropertyFilter, setLocationPropertyFilter, filteredLocations.length, 'location')}
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Location</th>
                    <th>Site address</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.length > 0 ? (
                    filteredLocations.map((location) => (
                      <tr key={location.id}>
                        <td>{location.id}</td>
                        <td>{location.site_name || location.name}</td>
                        <td>{location.site_address || '—'}</td>
                        <td>{location.property_name || '—'}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditLocationModal(location)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteLocationModal(location)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        {locations.length > 0
                          ? 'No locations for this property'
                          : 'No locations found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'Requester': {
        const filteredRequesters = filterByProperty(requesters, requesterPropertyFilter);
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Requester Management</h3>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openImportRequestersModal}
                >
                  Import from CSV
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddRequesterModal(true)}
                >
                  Add Requester
                </button>
              </div>
            </div>
            <p className="text-muted small">Manage requesters and their information by property.</p>
            {renderPropertyFilter(
              requesterPropertyFilter,
              setRequesterPropertyFilter,
              filteredRequesters.length,
              'requester'
            )}
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Requester Name</th>
                    <th>Contact</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRequesters.length > 0 ? (
                    filteredRequesters.map((requester) => (
                      <tr key={requester.id}>
                        <td>{requester.id}</td>
                        <td>{requester.name}</td>
                        <td>{requester.contact}</td>
                        <td>{requester.property_name}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditRequesterModal(requester)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteRequesterModal(requester)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        {requesters.length > 0
                          ? 'No requesters for this property'
                          : 'No requesters found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      case 'Assigned': {
        const filteredAssigned = filterByProperty(assigned, assignedPropertyFilter);
        return (
          <div>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
              <h3 className="mb-0">Assignment Management</h3>
              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={openImportAssignedModal}
                >
                  Import from CSV
                </button>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowAddAssignedModal(true)}
                >
                  Add Assignment
                </button>
              </div>
            </div>
            <p className="text-muted small">Manage technician assignments and workloads by property.</p>
            {renderPropertyFilter(
              assignedPropertyFilter,
              setAssignedPropertyFilter,
              filteredAssigned.length,
              'assignment'
            )}
            <div className="app-table-wrap table-responsive">
              <table className="table table-striped mb-0">
                <thead className="table-light">
                  <tr>
                    <th>ID</th>
                    <th>Technician</th>
                    <th>Assigned Requests</th>
                    <th>Property</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAssigned.length > 0 ? (
                    filteredAssigned.map((assignedItem) => (
                      <tr key={assignedItem.id}>
                        <td>{assignedItem.id}</td>
                        <td>{assignedItem.technician}</td>
                        <td>{assignedItem.assigned_requests}</td>
                        <td>{assignedItem.property_name}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            onClick={() => openEditAssignedModal(assignedItem)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => openDeleteAssignedModal(assignedItem)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center text-muted">
                        {assigned.length > 0
                          ? 'No assignments for this property'
                          : 'No assignments found'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      }

      default:
        return <div>Select a menu item to get started.</div>;
    }
  };

  const renderModal = () => {
    if (showAddUserModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New User</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddUserModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddUser}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newUser.name}
                      onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      value={newUser.email}
                      onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input 
                      type="password" 
                      className="form-control" 
                      value={newUser.password}
                      onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={newUser.role_id}
                      onChange={(e) => setNewUser({ ...newUser, role_id: e.target.value })}
                      required
                    >
                      <option value="">Select role…</option>
                      {rolesList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.display_name} ({r.key})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Property
                      {roleKeyById(newUser.role_id) === 'manager' ? <span className="text-danger"> *</span> : ' (Optional)'}
                    </label>
                    <select 
                      className="form-select" 
                      value={newUser.property_id}
                      onChange={(e) => setNewUser({...newUser, property_id: e.target.value})}
                    >
                      <option value="">No Property Assigned</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddUserModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showEditUserModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit User</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditUserModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditUser}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingUser.name}
                      onChange={(e) => setEditingUser({...editingUser, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Email</label>
                    <input 
                      type="email" 
                      className="form-control" 
                      value={editingUser.email}
                      onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Password</label>
                    <input 
                      type="password" 
                      className="form-control" 
                      value={editingUser.password}
                      onChange={(e) => setEditingUser({...editingUser, password: e.target.value})}
                      placeholder="Leave blank to keep current password"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Role</label>
                    <select
                      className="form-select"
                      value={editingUser.role_id}
                      onChange={(e) => setEditingUser({ ...editingUser, role_id: e.target.value })}
                      required
                    >
                      {rolesList.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.display_name} ({r.key})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-3">
                    <label className="form-label">
                      Property
                      {roleKeyById(editingUser.role_id) === 'manager' ? <span className="text-danger"> *</span> : ' (Optional)'}
                    </label>
                    <select 
                      className="form-select" 
                      value={editingUser.property_id || ''}
                      onChange={(e) => setEditingUser({...editingUser, property_id: e.target.value})}
                    >
                      <option value="">No Property Assigned</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditUserModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showDeleteUserModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete User</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteUserModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete user <strong>{deletingUser.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteUserModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteUser}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete User'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showEditPropertyModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Property</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditPropertyModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditProperty}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Property Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingProperty.name}
                      onChange={(e) => setEditingProperty({...editingProperty, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea 
                      className="form-control" 
                      value={editingProperty.address}
                      onChange={(e) => setEditingProperty({...editingProperty, address: e.target.value})}
                      rows="3"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingProperty.property_code}
                      onChange={(e) =>
                        setEditingProperty({ ...editingProperty, property_code: e.target.value })
                      }
                      placeholder="e.g. COVE-NORTH (for reporting / exports)"
                      autoComplete="off"
                    />
                    <div className="form-text">
                      Optional stable identifier; must be unique when set. Used to link exported data.
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditPropertyModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Property'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showDeletePropertyModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Property</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeletePropertyModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete property <strong>{deletingProperty.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone. All related data (statuses, locations, etc.) will also be deleted.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeletePropertyModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteProperty}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Property'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showAddPropertyModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Property</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddPropertyModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddProperty}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Property Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newProperty.name}
                      onChange={(e) => setNewProperty({...newProperty, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Address</label>
                    <textarea 
                      className="form-control" 
                      value={newProperty.address}
                      onChange={(e) => setNewProperty({...newProperty, address: e.target.value})}
                      rows="3"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property code</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newProperty.property_code}
                      onChange={(e) =>
                        setNewProperty({ ...newProperty, property_code: e.target.value })
                      }
                      placeholder="e.g. COVE-NORTH (for reporting / exports)"
                      autoComplete="off"
                    />
                    <div className="form-text">
                      Optional stable identifier; must be unique when set. Used to link exported data.
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddPropertyModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Property'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showAddStatusModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Status</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddStatusModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddStatus}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Status Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newStatus.name}
                      onChange={(e) => setNewStatus({...newStatus, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={newStatus.property_id}
                      onChange={(e) => setNewStatus({...newStatus, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddStatusModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showAddRequestTypeModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Request Type</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddRequestTypeModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddRequestType}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Request Type Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newRequestType.name}
                      onChange={(e) => setNewRequestType({...newRequestType, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={newRequestType.property_id}
                      onChange={(e) => setNewRequestType({...newRequestType, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddRequestTypeModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Request Type'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showAddLocationModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Location</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddLocationModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddLocation}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Location Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newLocation.name}
                      onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Site address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={newLocation.site_address}
                      onChange={(e) => setNewLocation({ ...newLocation, site_address: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select
                      className="form-select"
                      value={newLocation.property_id}
                      onChange={(e) => setNewLocation({ ...newLocation, property_id: e.target.value })}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowAddLocationModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showAddRequesterModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Requester</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddRequesterModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddRequester}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Requester Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newRequester.name}
                      onChange={(e) => setNewRequester({...newRequester, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Contact</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newRequester.contact}
                      onChange={(e) => setNewRequester({...newRequester, contact: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={newRequester.property_id}
                      onChange={(e) => setNewRequester({...newRequester, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddRequesterModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Requester'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    if (showAddAssignedModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add New Assignment</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowAddAssignedModal(false)}
                ></button>
              </div>
              <form onSubmit={handleAddAssigned}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Technician</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newAssigned.technician}
                      onChange={(e) => setNewAssigned({...newAssigned, technician: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Assigned Requests</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={newAssigned.assignedRequests}
                      onChange={(e) => setNewAssigned({...newAssigned, assignedRequests: parseInt(e.target.value)})}
                      min="0"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={newAssigned.property_id}
                      onChange={(e) => setNewAssigned({...newAssigned, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAddAssignedModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Creating...' : 'Create Assignment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Status Edit Modal
    if (showEditStatusModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Status</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditStatusModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditStatus}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Status Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingStatus.name}
                      onChange={(e) => setEditingStatus({...editingStatus, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={editingStatus.property_id}
                      onChange={(e) => setEditingStatus({...editingStatus, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditStatusModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Status'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Status Delete Modal
    if (showDeleteStatusModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Status</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteStatusModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete status <strong>{deletingStatus.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteStatusModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteStatus}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Status'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Request Type Edit Modal
    if (showEditRequestTypeModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Request Type</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditRequestTypeModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditRequestType}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Request Type Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingRequestType.name}
                      onChange={(e) => setEditingRequestType({...editingRequestType, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={editingRequestType.property_id}
                      onChange={(e) => setEditingRequestType({...editingRequestType, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditRequestTypeModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Request Type'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Request Type Delete Modal
    if (showDeleteRequestTypeModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Request Type</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteRequestTypeModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete request type <strong>{deletingRequestType.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteRequestTypeModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteRequestType}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Request Type'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Location Edit Modal
    if (showEditLocationModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Location</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditLocationModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditLocation}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Location Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingLocation.name}
                      onChange={(e) => setEditingLocation({ ...editingLocation, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Site address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editingLocation.site_address}
                      onChange={(e) => setEditingLocation({ ...editingLocation, site_address: e.target.value })}
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select
                      className="form-select"
                      value={editingLocation.property_id}
                      onChange={(e) => setEditingLocation({ ...editingLocation, property_id: e.target.value })}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map((property) => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditLocationModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Location'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Location Delete Modal
    if (showDeleteLocationModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Location</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteLocationModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete location <strong>{deletingLocation.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteLocationModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteLocation}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Location'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Requester Edit Modal
    if (showEditRequesterModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Requester</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditRequesterModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditRequester}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Requester Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingRequester.name}
                      onChange={(e) => setEditingRequester({...editingRequester, name: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Contact</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingRequester.contact}
                      onChange={(e) => setEditingRequester({...editingRequester, contact: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={editingRequester.property_id}
                      onChange={(e) => setEditingRequester({...editingRequester, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditRequesterModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Requester'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Requester Delete Modal
    if (showDeleteRequesterModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Requester</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteRequesterModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete requester <strong>{deletingRequester.name}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteRequesterModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteRequester}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Requester'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Assigned Edit Modal
    if (showEditAssignedModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Assignment</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowEditAssignedModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditAssigned}>
                <div className="modal-body">
                  {error && (
                    <div className="alert alert-danger">
                      {error}
                    </div>
                  )}
                  
                  <div className="mb-3">
                    <label className="form-label">Technician</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={editingAssigned.technician}
                      onChange={(e) => setEditingAssigned({...editingAssigned, technician: e.target.value})}
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Assigned Requests</label>
                    <input 
                      type="number" 
                      className="form-control" 
                      value={editingAssigned.assignedRequests}
                      onChange={(e) => setEditingAssigned({...editingAssigned, assignedRequests: parseInt(e.target.value)})}
                      min="0"
                      required
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Property</label>
                    <select 
                      className="form-select" 
                      value={editingAssigned.property_id}
                      onChange={(e) => setEditingAssigned({...editingAssigned, property_id: e.target.value})}
                      required
                    >
                      <option value="">Select a property</option>
                      {properties.map(property => (
                        <option key={property.id} value={property.id}>
                          {property.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowEditAssignedModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Updating...' : 'Update Assignment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      );
    }

    // Assigned Delete Modal
    if (showDeleteAssignedModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete Assignment</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowDeleteAssignedModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                <p>Are you sure you want to delete assignment for <strong>{deletingAssigned.technician}</strong>?</p>
                <p className="text-muted">This action cannot be undone.</p>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDeleteAssignedModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteAssigned}
                  disabled={loading}
                >
                  {loading ? 'Deleting...' : 'Delete Assignment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showImportAssignedModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import assigned technicians</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportAssignedModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with a <strong>Title</strong> column (same format as PSR Assigned.csv).
                  Each title becomes a technician name for the property you select below. Assigned
                  request count starts at 0.
                </p>
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={assignedImportPropertyId}
                    onChange={(e) => setAssignedImportPropertyId(e.target.value)}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={downloadAssignedImportTemplate}
                >
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportAssignedFile}
                  />
                </div>
                {assignedImportParseError && (
                  <div className="alert alert-danger py-2 small">{assignedImportParseError}</div>
                )}
                {assignedImportRows.length > 0 && !assignedImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{assignedImportRows.length}</strong> technician
                    {assignedImportRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {assignedImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{assignedImportResult.created}</strong> created,{' '}
                      <strong>{assignedImportResult.skipped}</strong> skipped.
                    </p>
                    {assignedImportResult.errors?.length > 0 && (
                      <div className="table-responsive border rounded" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Title</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {assignedImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.title ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {assignedImportResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportAssignedModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    assignedImportBusy ||
                    !assignedImportPropertyId ||
                    !assignedImportRows.length ||
                    !!assignedImportParseError
                  }
                  onClick={handleRunAssignedImport}
                >
                  {assignedImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showImportRequestersModal) {
      const importableCount = requesterImportRows.filter((r) => {
        if (!r.title) return false;
        if (r.enabled == null || String(r.enabled).trim() === '') return true;
        const s = String(r.enabled).trim().toLowerCase();
        return !['false', '0', 'no', 'n'].includes(s);
      }).length;
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import requesters</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportRequestersModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with a <strong>Title</strong> column (same format as PSR Requester.csv).
                  Optional <strong>Enabled</strong> (<code>Yes</code>/<code>No</code>): rows marked No are
                  skipped. Each title becomes a requester name for the property you select below.
                </p>
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={requesterImportPropertyId}
                    onChange={(e) => setRequesterImportPropertyId(e.target.value)}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={downloadRequesterImportTemplate}
                >
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportRequestersFile}
                  />
                </div>
                {requesterImportParseError && (
                  <div className="alert alert-danger py-2 small">{requesterImportParseError}</div>
                )}
                {requesterImportRows.length > 0 && !requesterImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    <strong>{requesterImportRows.length}</strong> row
                    {requesterImportRows.length === 1 ? '' : 's'} in file,{' '}
                    <strong>{importableCount}</strong> will be imported (Enabled = Yes or blank).
                  </div>
                )}
                {requesterImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{requesterImportResult.created}</strong> created,{' '}
                      <strong>{requesterImportResult.skipped}</strong> skipped.
                    </p>
                    {requesterImportResult.errors?.length > 0 && (
                      <div className="table-responsive border rounded" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Title</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requesterImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.title ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {requesterImportResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportRequestersModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    requesterImportBusy ||
                    !requesterImportPropertyId ||
                    !requesterImportRows.length ||
                    !!requesterImportParseError ||
                    importableCount === 0
                  }
                  onClick={handleRunRequesterImport}
                >
                  {requesterImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showImportStatusesModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import statuses</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportStatusesModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with a <strong>Title</strong> column (status name). Each title is
                  created for the property you select below (e.g. Open, Completed, Follow Up).
                </p>
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={statusImportPropertyId}
                    onChange={(e) => setStatusImportPropertyId(e.target.value)}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={downloadStatusImportTemplate}
                >
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportStatusesFile}
                  />
                </div>
                {statusImportParseError && (
                  <div className="alert alert-danger py-2 small">{statusImportParseError}</div>
                )}
                {statusImportRows.length > 0 && !statusImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{statusImportRows.length}</strong> status
                    {statusImportRows.length === 1 ? '' : 'es'}.
                  </div>
                )}
                {statusImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{statusImportResult.created}</strong> created,{' '}
                      <strong>{statusImportResult.skipped}</strong> skipped.
                    </p>
                    {statusImportResult.errors?.length > 0 && (
                      <div className="table-responsive border rounded" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Title</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statusImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.title ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {statusImportResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportStatusesModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    statusImportBusy ||
                    !statusImportPropertyId ||
                    !statusImportRows.length ||
                    !!statusImportParseError
                  }
                  onClick={handleRunStatusImport}
                >
                  {statusImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (showImportRequestTypesModal) {
      return (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Import request types</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowImportRequestTypesModal(false)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="small text-muted">
                  Upload a CSV with a <strong>Title</strong> column (same format as Request Types.csv).
                  Optional <strong>Category</strong> is ignored. All imported titles are assigned to the
                  property you select below.
                </p>
                <div className="mb-3">
                  <label className="form-label">Property</label>
                  <select
                    className="form-select"
                    value={requestTypeImportPropertyId}
                    onChange={(e) => setRequestTypeImportPropertyId(e.target.value)}
                    required
                  >
                    <option value="">Select property…</option>
                    {properties.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary mb-3"
                  onClick={downloadRequestTypeImportTemplate}
                >
                  Download sample CSV
                </button>
                <div className="mb-3">
                  <label className="form-label">CSV file</label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    className="form-control"
                    onChange={handleImportRequestTypesFile}
                  />
                </div>
                {requestTypeImportParseError && (
                  <div className="alert alert-danger py-2 small">{requestTypeImportParseError}</div>
                )}
                {requestTypeImportRows.length > 0 && !requestTypeImportParseError && (
                  <div className="alert alert-info py-2 small mb-0">
                    Ready to import <strong>{requestTypeImportRows.length}</strong> title
                    {requestTypeImportRows.length === 1 ? '' : 's'}.
                  </div>
                )}
                {requestTypeImportResult && (
                  <div className="mt-3">
                    <p className="mb-2 small">
                      <strong>{requestTypeImportResult.created}</strong> created,{' '}
                      <strong>{requestTypeImportResult.skipped}</strong> skipped.
                    </p>
                    {requestTypeImportResult.errors?.length > 0 && (
                      <div className="table-responsive border rounded" style={{ maxHeight: 280, overflow: 'auto' }}>
                        <table className="table table-sm table-bordered mb-0">
                          <thead>
                            <tr>
                              <th>Row</th>
                              <th>Title</th>
                              <th>Error</th>
                            </tr>
                          </thead>
                          <tbody>
                            {requestTypeImportResult.errors.slice(0, 50).map((er, idx) => (
                              <tr key={`${er.line}-${idx}`}>
                                <td>{er.line}</td>
                                <td>{er.title ?? '—'}</td>
                                <td className="small">{er.error}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {requestTypeImportResult.errors.length > 50 && (
                          <p className="small text-muted mt-1 mb-0">Showing first 50 errors.</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowImportRequestTypesModal(false)}
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={
                    requestTypeImportBusy ||
                    !requestTypeImportPropertyId ||
                    !requestTypeImportRows.length ||
                    !!requestTypeImportParseError
                  }
                  onClick={handleRunRequestTypeImport}
                >
                  {requestTypeImportBusy ? 'Importing…' : 'Run import'}
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  const isAnyModalOpen = showAddUserModal || showEditUserModal || showDeleteUserModal || 
                        showAddPropertyModal || showEditPropertyModal || showDeletePropertyModal ||
                        showAddStatusModal || showEditStatusModal || showDeleteStatusModal ||
                        showImportStatusesModal ||
                        showAddRequestTypeModal || showEditRequestTypeModal || showDeleteRequestTypeModal ||
                        showImportRequestTypesModal ||
                        showImportRequestersModal ||
                        showImportAssignedModal ||
                        showAddLocationModal || showEditLocationModal || showDeleteLocationModal ||
                        showAddRequesterModal || showEditRequesterModal || showDeleteRequesterModal ||
                        showAddAssignedModal || showEditAssignedModal || showDeleteAssignedModal;

  return (
    <div className="container-fluid mt-4 app-shell-page">
      <div className="row">
        <div className="col-12">
          <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h3 mb-1">Service Requests Admin Panel</h1>
              <p className="text-muted small mb-0">Master data for users, properties, lookups, and assignments.</p>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 flex-shrink-0">
              <span className="small text-muted">Welcome, {user?.name} ({user?.role_key || user?.role})</span>
              <button
                type="button"
                onClick={() => navigate(ROUTES.SERVICE_REQUESTS)}
                className="btn btn-outline-secondary btn-sm"
              >
                Dashboard
              </button>
              <button type="button" onClick={handleLogout} className="btn btn-outline-danger btn-sm">
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4 admin-sidebar-layout">
        {/* Left Sidebar */}
        <div className="col-md-3">
          <div className="card app-card">
            <div className="card-header bg-transparent border-bottom py-3">
              <h5 className="mb-0">Admin Menu</h5>
            </div>
            <div className="list-group list-group-flush">
              {menuItems.map((item) => (
                <button
                  key={item}
                  className={`list-group-item list-group-item-action ${
                    activeMenu === item ? 'active' : ''
                  }`}
                  onClick={() => setActiveMenu(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="col-md-9">
          <div className="card app-card">
            <div className="card-body">
              {renderContent()}
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {renderModal()}

      {/* Modal Backdrop */}
      {isAnyModalOpen && (
        <div 
          className="modal-backdrop fade show" 
          onClick={() => {
            setShowAddUserModal(false);
            setShowEditUserModal(false);
            setShowDeleteUserModal(false);
            setShowAddPropertyModal(false);
            setShowEditPropertyModal(false);
            setShowDeletePropertyModal(false);
            setShowAddStatusModal(false);
            setShowEditStatusModal(false);
            setShowDeleteStatusModal(false);
            setShowImportStatusesModal(false);
            setShowAddRequestTypeModal(false);
            setShowEditRequestTypeModal(false);
            setShowDeleteRequestTypeModal(false);
            setShowImportRequestTypesModal(false);
            setShowImportRequestersModal(false);
            setShowImportAssignedModal(false);
            setShowAddLocationModal(false);
            setShowEditLocationModal(false);
            setShowDeleteLocationModal(false);
            setShowAddRequesterModal(false);
            setShowEditRequesterModal(false);
            setShowDeleteRequesterModal(false);
            setShowAddAssignedModal(false);
            setShowEditAssignedModal(false);
            setShowDeleteAssignedModal(false);
          }}
        ></div>
      )}
    </div>
  );
}

export default Admin; 